import { useState, useEffect } from 'react';
import type { CalendarEvent, Theme } from '../types';
import { Timestamp } from 'firebase/firestore';

interface CalendarViewProps {
  events: CalendarEvent[];
  onAddEvent: (event: Omit<CalendarEvent, 'id'>) => Promise<void>;
  onDeleteEvent: (eventId: string) => Promise<void>;
  currentPerson: 'tim' | 'diya';
  theme: Theme;
}

const CalendarView = ({ events, onAddEvent, onDeleteEvent, currentPerson, theme }: CalendarViewProps) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'add'>('list');
  const [webcalUrl, setWebcalUrl] = useState<string>('');
  
  // Form state
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    type: 'meeting' as CalendarEvent['type']
  });

  // Generate webcal URL for calendar subscription
  useEffect(() => {
    // In production, you'd generate this from your backend
    // For now, we'll create a blob URL with the calendar feed
    const generateCalendarFeed = () => {
      const icsEvents = events.map(event => {
        const eventDate = event.date.toDate();
        const formatDate = (date: Date) => {
          return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        };

        return [
          'BEGIN:VEVENT',
          `UID:${event.id}@yourdomain.com`,
          `DTSTART:${formatDate(eventDate)}`,
          `DTEND:${formatDate(new Date(eventDate.getTime() + 60 * 60 * 1000))}`,
          `SUMMARY:${event.title}`,
          event.description ? `DESCRIPTION:${event.description}` : '',
          event.location ? `LOCATION:${event.location}` : '',
          `CREATED:${formatDate(new Date())}`,
          'END:VEVENT'
        ].filter(Boolean).join('\r\n');
      }).join('\r\n');

      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Valentine Calendar//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'X-WR-CALNAME:Our Special Calendar',
        'X-WR-TIMEZONE:UTC',
        'X-WR-CALDESC:Shared calendar for our special moments',
        icsEvents,
        'END:VCALENDAR'
      ].join('\r\n');

      return icsContent;
    };

    const icsContent = generateCalendarFeed();
    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    setWebcalUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [events]);

  const daysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const firstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getEventsForDate = (date: Date) => {
    return events.filter(event => {
      const eventDate = event.date.toDate();
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleDateClick = (day: number) => {
    const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    setSelectedDate(clickedDate);
    
    const dayEvents = getEventsForDate(clickedDate);
    
    const year = clickedDate.getFullYear();
    const month = String(clickedDate.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    setEventForm({
      ...eventForm,
      date: `${year}-${month}-${dayStr}`
    });
    
    if (dayEvents.length > 0) {
      setViewMode('list');
    } else {
      setViewMode('add');
    }
    
    setShowEventModal(true);
  };

  const handleAddEvent = async () => {
    if (!eventForm.title || !eventForm.date) return;

    const dateTime = new Date(`${eventForm.date}T${eventForm.time || '00:00'}`);
    
    await onAddEvent({
      title: eventForm.title,
      description: eventForm.description,
      date: Timestamp.fromDate(dateTime),
      location: eventForm.location,
      type: eventForm.type,
      createdBy: currentPerson
    });

    resetForm();
    setShowEventModal(false);
  };

  const resetForm = () => {
    setEventForm({
      title: '',
      description: '',
      date: '',
      time: '',
      location: '',
      type: 'meeting'
    });
    setSelectedDate(null);
  };

  const exportToICalendar = (event: CalendarEvent) => {
    const eventDate = event.date.toDate();
    const formatDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Valentine Calendar//EN',
      'BEGIN:VEVENT',
      `UID:${event.id}@yourdomain.com`,
      `DTSTART:${formatDate(eventDate)}`,
      `DTEND:${formatDate(new Date(eventDate.getTime() + 60 * 60 * 1000))}`,
      `SUMMARY:${event.title}`,
      event.description ? `DESCRIPTION:${event.description}` : '',
      event.location ? `LOCATION:${event.location}` : '',
      'END:VEVENT',
      'END:VCALENDAR'
    ].filter(Boolean).join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${event.title.replace(/\s+/g, '_')}.ics`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportAllToICalendar = () => {
    const icsEvents = events.map(event => {
      const eventDate = event.date.toDate();
      const formatDate = (date: Date) => {
        return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      };

      return [
        'BEGIN:VEVENT',
        `UID:${event.id}@yourdomain.com`,
        `DTSTART:${formatDate(eventDate)}`,
        `DTEND:${formatDate(new Date(eventDate.getTime() + 60 * 60 * 1000))}`,
        `SUMMARY:${event.title}`,
        event.description ? `DESCRIPTION:${event.description}` : '',
        event.location ? `LOCATION:${event.location}` : '',
        'END:VEVENT'
      ].filter(Boolean).join('\r\n');
    }).join('\r\n');

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Valentine Calendar//EN',
      'CALSCALE:GREGORIAN',
      'X-WR-CALNAME:Our Special Calendar',
      icsEvents,
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'our_calendar.ics';
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportToGoogleCalendar = (event: CalendarEvent) => {
    const startDate = event.date.toDate();
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);

    const formatGoogleDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const googleCalendarUrl = new URL('https://calendar.google.com/calendar/render');
    googleCalendarUrl.searchParams.set('action', 'TEMPLATE');
    googleCalendarUrl.searchParams.set('text', event.title);
    googleCalendarUrl.searchParams.set('dates', `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`);
    googleCalendarUrl.searchParams.set('details', event.description || '');
    googleCalendarUrl.searchParams.set('location', event.location || '');

    window.open(googleCalendarUrl.toString(), '_blank');
  };

  const exportToOutlook = (event: CalendarEvent) => {
    const startDate = event.date.toDate();
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);

    const formatOutlookDate = (date: Date) => {
      return date.toISOString();
    };

    const outlookUrl = new URL('https://outlook.live.com/calendar/0/deeplink/compose');
    outlookUrl.searchParams.set('path', '/calendar/action/compose');
    outlookUrl.searchParams.set('rru', 'addevent');
    outlookUrl.searchParams.set('subject', event.title);
    outlookUrl.searchParams.set('startdt', formatOutlookDate(startDate));
    outlookUrl.searchParams.set('enddt', formatOutlookDate(endDate));
    outlookUrl.searchParams.set('body', event.description || '');
    outlookUrl.searchParams.set('location', event.location || '');

    window.open(outlookUrl.toString(), '_blank');
  };

  const copyWebcalUrl = () => {
    const productionWebcalUrl = 'webcal://diya.timkolesnichenko.me/calendar/feed.ics';
    navigator.clipboard.writeText(productionWebcalUrl);
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const renderCalendar = () => {
    const days = [];
    const totalDays = daysInMonth(currentDate);
    const firstDay = firstDayOfMonth(currentDate);

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="aspect-square p-2" />);
    }

    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayEvents = getEventsForDate(date);
      const isToday = date.toDateString() === new Date().toDateString();
      const isPast = date < new Date() && !isToday;

      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          className={`aspect-square p-2 rounded-lg transition-all hover:scale-105 relative ${
            isToday ? 'bg-gradient-to-br ' + theme.primary + ' text-white font-bold' :
            isPast ? 'opacity-40' :
            dayEvents.length > 0 ? theme.secondary + ' font-semibold' :
            'bg-white hover:' + theme.secondary
          }`}
        >
          <div className="text-sm">{day}</div>
          {dayEvents.length > 0 && (
            <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex gap-1">
              {dayEvents.slice(0, 3).map((_, idx) => (
                <div key={idx} className={`w-1.5 h-1.5 rounded-full ${
                  isToday ? 'bg-white' : 'bg-gradient-to-r ' + theme.primary
                }`} />
              ))}
            </div>
          )}
        </button>
      );
    }

    return days;
  };

  const getNextEvent = () => {
    const now = new Date();
    const futureEvents = events
      .filter(e => e.date.toDate() > now)
      .sort((a, b) => a.date.toMillis() - b.date.toMillis());
    return futureEvents[0] || null;
  };

  const nextEvent = getNextEvent();
  const selectedDayEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  return (
    <div className={`${theme.secondary} rounded-2xl p-6 shadow-lg`}>
      {/* Next Event Countdown */}
      {nextEvent && <NextEventCountdown event={nextEvent} theme={theme} />}

      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={handlePrevMonth} className="p-2 hover:bg-white rounded-lg transition-colors">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h2 className={`text-2xl font-bold ${theme.text}`}>
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        <button onClick={handleNextMonth} className="p-2 hover:bg-white rounded-lg transition-colors">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Day Names */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {dayNames.map(day => (
          <div key={day} className={`text-center text-sm font-semibold ${theme.text} opacity-70`}>
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2 mb-6">
        {renderCalendar()}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mb-4">
        <button
          onClick={() => {
            resetForm();
            setViewMode('add');
            setShowEventModal(true);
          }}
          className={`flex-1 ${theme.button} text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Event
        </button>
        <button
          onClick={() => setShowSyncModal(true)}
          className="bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center gap-2 shadow"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Sync
        </button>
      </div>

      {/* Sync Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold mb-2">Sync Calendar</h3>
                <p className="text-gray-600 text-sm">Connect your calendar apps to stay synced</p>
              </div>
              <button
                onClick={() => setShowSyncModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Export All */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold mb-2">📥 Export All Events</h4>
                <p className="text-sm text-gray-600 mb-3">Download a file with all events to import into any calendar app</p>
                <button
                  onClick={exportAllToICalendar}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Download .ics File
                </button>
              </div>

              {/* Subscribe (Auto-sync) */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold mb-2">🔄 Auto-Sync (Subscribe)</h4>
                <p className="text-sm text-gray-600 mb-3">Subscribe to keep your calendar automatically updated</p>
                
                <div className="space-y-2">
                  {/* Apple Calendar */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                        
                      </div>
                      <div>
                        <div className="font-medium text-sm">Apple Calendar</div>
                        <div className="text-xs text-gray-500">iPhone, iPad, Mac</div>
                      </div>
                    </div>
                    <a
                      href={webcalUrl.replace('blob:', 'webcal:')}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      Subscribe
                    </a>
                  </div>

                  {/* Google Calendar */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-red-400 via-yellow-400 to-green-400 rounded-lg flex items-center justify-center text-white font-bold">
                        G
                      </div>
                      <div>
                        <div className="font-medium text-sm">Google Calendar</div>
                        <div className="text-xs text-gray-500">Add by URL</div>
                      </div>
                    </div>
                    <button
                      onClick={copyWebcalUrl}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      Copy URL
                    </button>
                  </div>

                  {/* Outlook */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center text-white font-bold">
                        O
                      </div>
                      <div>
                        <div className="font-medium text-sm">Outlook</div>
                        <div className="text-xs text-gray-500">Desktop & Web</div>
                      </div>
                    </div>
                    <button
                      onClick={copyWebcalUrl}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      Copy URL
                    </button>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2 text-sm">📝 How to Subscribe</h4>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li>• <strong>Apple:</strong> Tap "Subscribe" on iPhone/iPad, or click on Mac</li>
                  <li>• <strong>Google:</strong> Copy URL → Calendar Settings → "Add calendar" → "From URL"</li>
                  <li>• <strong>Outlook:</strong> Copy URL → Add Calendar → Subscribe from web</li>
                </ul>
                <p className="text-xs text-blue-800 mt-2 font-medium">
                  ✨ Updates automatically when events change!
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowSyncModal(false)}
              className="w-full mt-4 bg-gray-200 text-gray-800 px-4 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            {viewMode === 'list' && selectedDayEvents.length > 0 ? (
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold">
                      {selectedDate?.toLocaleDateString(undefined, { 
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">{selectedDayEvents.length} event{selectedDayEvents.length !== 1 ? 's' : ''}</p>
                  </div>
                  <button
                    onClick={() => setShowEventModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  {selectedDayEvents.map((event) => (
                    <div key={event.id} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-bold text-lg">{event.title}</h4>
                        <span className="px-2 py-1 bg-gray-100 rounded-full text-xs capitalize">
                          {event.type}
                        </span>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>{event.date.toDate().toLocaleTimeString(undefined, {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}</span>
                        </div>

                        {event.location && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>{event.location}</span>
                          </div>
                        )}

                        {event.description && (
                          <p className="text-gray-700 mt-2">{event.description}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-4 gap-2 mt-3">
                        <button
                          onClick={() => exportToICalendar(event)}
                          className="bg-gray-500 text-white px-2 py-1.5 rounded text-xs hover:bg-gray-600 transition-colors"
                          title="Download .ics file"
                        >
                          .ics
                        </button>
                        <button
                          onClick={() => exportToGoogleCalendar(event)}
                          className="bg-blue-500 text-white px-2 py-1.5 rounded text-xs hover:bg-blue-600 transition-colors"
                          title="Add to Google Calendar"
                        >
                          Google
                        </button>
                        <button
                          onClick={() => exportToOutlook(event)}
                          className="bg-blue-600 text-white px-2 py-1.5 rounded text-xs hover:bg-blue-700 transition-colors"
                          title="Add to Outlook"
                        >
                          Outlook
                        </button>
                        <button
                          onClick={async () => {
                            await onDeleteEvent(event.id);
                            if (selectedDayEvents.length === 1) {
                              setShowEventModal(false);
                            }
                          }}
                          className="bg-red-500 text-white px-2 py-1.5 rounded text-xs hover:bg-red-600 transition-colors"
                          title="Delete event"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setViewMode('add')}
                  className={`w-full ${theme.button} text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Another Event
                </button>
              </div>
            ) : (
              <div>
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-2xl font-bold">Add Event</h3>
                  <button
                    onClick={() => setShowEventModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Title *</label>
                    <input
                      type="text"
                      value={eventForm.title}
                      onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                      placeholder="Dinner date, Video call..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Date *</label>
                      <input
                        type="date"
                        value={eventForm.date}
                        onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">Time</label>
                      <input
                        type="time"
                        value={eventForm.time}
                        onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Type</label>
                    <select
                      value={eventForm.type}
                      onChange={(e) => setEventForm({ ...eventForm, type: e.target.value as CalendarEvent['type'] })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="meeting">Meeting</option>
                      <option value="call">Video Call</option>
                      <option value="special">Special Occasion</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Location</label>
                    <input
                      type="text"
                      value={eventForm.location}
                      onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                      placeholder="Restaurant name, video call link..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Description</label>
                    <textarea
                      value={eventForm.description}
                      onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                      placeholder="Any special details..."
                      rows={3}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      if (selectedDayEvents.length > 0) {
                        setViewMode('list');
                      } else {
                        setShowEventModal(false);
                      }
                    }}
                    className="flex-1 bg-gray-200 text-gray-800 px-4 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                  >
                    {selectedDayEvents.length > 0 ? 'Back' : 'Cancel'}
                  </button>
                  <button
                    onClick={handleAddEvent}
                    disabled={!eventForm.title || !eventForm.date}
                    className={`flex-1 ${theme.button} text-white px-4 py-3 rounded-lg transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    Add Event
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const NextEventCountdown = ({ event, theme }: { event: CalendarEvent; theme: Theme }) => {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date().getTime();
      const eventTime = event.date.toDate().getTime();
      const distance = eventTime - now;

      if (distance < 0) {
        setTimeLeft(null);
        return;
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [event]);

  if (!timeLeft) return null;

  return (
    <div className="mb-6 p-6 bg-white rounded-xl shadow">
      <div className="text-sm opacity-70 mb-2">Next Event</div>
      <div className={`font-bold text-xl ${theme.text} mb-3`}>{event.title}</div>
      
      <div className="grid grid-cols-4 gap-3 mb-3">
        {[
          { label: 'Days', value: timeLeft.days },
          { label: 'Hours', value: timeLeft.hours },
          { label: 'Minutes', value: timeLeft.minutes },
          { label: 'Seconds', value: timeLeft.seconds }
        ].map((item) => (
          <div key={item.label} className={`${theme.secondary} rounded-lg p-3 text-center`}>
            <div className={`text-2xl font-bold ${theme.accent}`}>
              {item.value}
            </div>
            <div className={`text-xs ${theme.text} opacity-70`}>
              {item.label}
            </div>
          </div>
        ))}
      </div>
      
      <div className={`text-sm ${theme.text} opacity-70`}>
        {event.date.toDate().toLocaleDateString(undefined, { 
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}
      </div>
    </div>
  );
};

export default CalendarView;